import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoyaltyBadge, { TierBadge } from "@/components/LoyaltyBadge";
import { Loader2, ArrowLeft, Phone, Hash, Gift, User, Save } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [flag, setFlag] = useState("");
  const [notes, setNotes] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = () => api.get(`/customers/${id}`).then(({ data }) => setData(data));

  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (data?.customer) {
      setFlag(data.customer.flag || "");
      setNotes(data.customer.notes || "");
    }
  }, [data]);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.patch(`/customers/${id}`, { flag, notes });
      toast.success("Profile note saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingNote(false);
    }
  };

  const redeem = async () => {
    setRedeeming(true);
    try {
      await api.post(`/loyalty/${id}/redeem`);
      toast.success("Free night redeemed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Redeem failed");
    } finally {
      setRedeeming(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const { customer, loyalty, stays, summary } = data;

  return (
    <div className="px-4 py-6 md:px-8">
      <button
        data-testid="profile-back"
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#A0148C]">
            <User className="h-7 w-7 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 data-testid="profile-name" className="font-heading text-3xl font-black tracking-tight text-slate-50">
                {customer.name}
              </h1>
              {loyalty && <TierBadge tier={loyalty.tier} />}
              {customer.flag && <FlagBadge flag={customer.flag} />}
            </div>
            <p className="flex items-center gap-3 text-slate-400">
              <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> {customer.phone}</span>
              {customer.plate && <span className="flex items-center gap-1"><Hash className="h-4 w-4" /> {customer.plate}</span>}
            </p>
          </div>
        </div>
        {loyalty && loyalty.availableFreeNights > 0 && (
          <Button
            data-testid="profile-redeem"
            onClick={redeem}
            disabled={redeeming}
            className="gap-2 bg-[#A0148C] hover:bg-[#80106F]"
          >
            {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            Redeem free night
          </Button>
        )}
      </div>

      {loyalty && <LoyaltyBadge loyalty={loyalty} className="mb-6" />}

      {/* Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900 p-5">
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-100">Carrier IDs</h2>
          <div className="space-y-2 font-data text-sm">
            <Line label="MC #" value={customer.mcNumber} />
            <Line label="DOT #" value={customer.dotNumber} />
            <Line label="Lic #" value={customer.licNumber} />
          </div>
        </Card>
        <Card className="border-slate-800 bg-slate-900 p-5">
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-100">Stays</h2>
          <div className="grid grid-cols-2 gap-3 text-center">
            <Mini label="Total" value={summary.totalStays} />
            <Mini label="Active" value={summary.activeStays} />
            <Mini label="Completed" value={summary.closedStays} />
            <Mini label="Overstays" value={summary.overstays} cls={summary.overstays ? "text-red-400" : "text-slate-100"} />
          </div>
        </Card>
        <Card className="border-slate-800 bg-slate-900 p-5">
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-100">Loyalty</h2>
          {loyalty ? (
            <div className="grid grid-cols-2 gap-3 text-center">
              <Mini label="Points" value={loyalty.pointsBalance} cls="text-[#A0148C]" />
              <Mini label="Lifetime Nights" value={loyalty.nightsStayed} />
              <Mini label="Free Ready" value={loyalty.availableFreeNights} cls="text-emerald-400" />
              <Mini label="Redeemed" value={loyalty.freeNightsRedeemed} />
            </div>
          ) : (
            <p className="text-slate-500">No loyalty account.</p>
          )}
        </Card>
      </div>

      {/* Profile note / flag editor */}
      <Card data-testid="profile-note-card" className="mb-6 border-slate-800 bg-slate-900 p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-100">Staff note &amp; flag</h2>
          <Button
            data-testid="profile-note-save"
            size="sm"
            onClick={saveNote}
            disabled={savingNote}
            className="gap-2 bg-[#A0148C] hover:bg-[#80106F]"
          >
            {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="md:w-56">
            <span className="mb-1 block text-xs text-slate-500">Flag</span>
            <div className="flex gap-2">
              {[["", "None"], ["VIP", "VIP"], ["WatchList", "Watch"]].map(([val, lbl]) => (
                <button
                  key={val || "none"}
                  data-testid={`profile-flag-${val || "none"}`}
                  onClick={() => setFlag(val)}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold ${
                    flag === val
                      ? val === "VIP"
                        ? "bg-yellow-500 text-black"
                        : val === "WatchList"
                        ? "bg-red-600 text-white"
                        : "bg-slate-600 text-white"
                      : "bg-slate-800 text-slate-300"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <span className="mb-1 block text-xs text-slate-500">Note (shown to staff at check-in)</span>
            <textarea
              data-testid="profile-note-text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Prefers north gate. Call on arrival."
              className="w-full rounded-md border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100"
            />
          </div>
        </div>
      </Card>

      {/* Stay history */}
      <h2 className="mb-3 font-heading text-2xl font-bold text-slate-100">Stay history</h2>
      <Card className="border-slate-800 bg-slate-900 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              {["Check-in", "Check-out", "Lot", "Spot", "Tenancy", "Amount", "Method", "Status"].map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-slate-400">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {stays.map((s) => (
              <TableRow key={s.id} data-testid={`profile-stay-${s.id}`} className="border-slate-800 hover:bg-slate-800/50">
                <TableCell className="whitespace-nowrap font-data text-xs text-slate-300">{fmt(s.checkInAt)}</TableCell>
                <TableCell className="whitespace-nowrap font-data text-xs text-slate-300">{fmt(s.checkOutAt)}</TableCell>
                <TableCell className="text-slate-300">{s.lotName}</TableCell>
                <TableCell className="font-data text-slate-300">#{s.spotNumber}</TableCell>
                <TableCell><Badge className={s.tenancyType === "Monthly" ? "bg-blue-600" : "bg-slate-700"}>{s.tenancyType}</Badge></TableCell>
                <TableCell className="font-data text-emerald-400">${(s.amountCharged || 0).toFixed(2)}</TableCell>
                <TableCell className="text-slate-300">{s.paymentMethod}</TableCell>
                <TableCell>
                  {s.overstayed ? (
                    <Badge className="bg-red-600">Overstayed</Badge>
                  ) : s.status === "Closed" ? (
                    <Badge className="bg-slate-700">Closed</Badge>
                  ) : (
                    <Badge className="bg-emerald-600">{s.status}</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {stays.length === 0 && (
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableCell colSpan={8} className="py-12 text-center text-slate-500">No stays on record.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function FlagBadge({ flag }) {
  const cls = flag === "VIP" ? "bg-yellow-500 text-black" : "bg-red-600 text-white";
  return (
    <span data-testid="profile-flag-badge" className={`rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>
      {flag === "WatchList" ? "Watch-list" : flag}
    </span>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-100">{value || "—"}</span>
    </div>
  );
}

function Mini({ label, value, cls = "text-slate-100" }) {
  return (
    <div>
      <p className={`font-data text-2xl font-bold ${cls}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}